import React, { Component } from 'react';
import { reviewService } from '../../_services/review.service';
import toast from '../shared/Toast';
import './ReviewModal.css';

class ReviewModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            step: 1, // 1 = stylist review, 2 = salon review, 3 = success
            stylistRating: 0,
            stylistReview: '',
            salonRating: 0,
            salonReview: '',
            stylistHover: 0,
            salonHover: 0,
            submitting: false,
            submitted: false
        };
    }

    handleStylistRating = (rating) => {
        this.setState({ stylistRating: rating });
    };

    handleSalonRating = (rating) => {
        this.setState({ salonRating: rating });
    };

    handleStylistHover = (rating) => {
        this.setState({ stylistHover: rating });
    };

    handleSalonHover = (rating) => {
        this.setState({ salonHover: rating });
    };

    handleNextStep = () => {
        const { step, stylistRating } = this.state;
        
        if (step === 1 && stylistRating === 0) {
            toast.error('Please select a rating for your stylist');
            return;
        }
        
        this.setState({ step: step + 1 });
    };

    handlePrevStep = () => {
        this.setState({ step: this.state.step - 1 });
    };

    handleSubmit = async () => {
        const { stylistRating, stylistReview, salonRating, salonReview } = this.state;
        const { booking, onClose, onReviewSubmitted } = this.props;

        if (salonRating === 0) {
            toast.error('Please select a rating for the salon');
            return;
        }

        this.setState({ submitting: true });

        try {
            const response = await reviewService.submitBothReviews({
                bookingId: booking._id,
                hairdresserRating: stylistRating,
                hairdresserReview: stylistReview,
                salonRating: salonRating,
                salonReview: salonReview
            });

            if (response.statusCode === 1) {
                this.setState({ step: 3, submitted: true, submitting: false });
                if (onReviewSubmitted) {
                    onReviewSubmitted(booking._id);
                }
            } else {
                toast.error(response.error?.responseMessage || 'Failed to submit review');
                this.setState({ submitting: false });
            }
        } catch (error) {
            console.error('Error submitting review:', error);
            toast.error('Failed to submit review. Please try again.');
            this.setState({ submitting: false });
        }
    };

    renderStars = (rating, hoverRating, onRate, onHover, size = 'large') => {
        const stars = [];
        const displayRating = hoverRating || rating;

        for (let i = 1; i <= 5; i++) {
            stars.push(
                <span
                    key={i}
                    className={`review-star ${size} ${i <= displayRating ? 'filled' : 'empty'}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onRate(i);
                    }}
                    onMouseEnter={() => onHover(i)}
                    onMouseLeave={() => onHover(0)}
                >
                    ★
                </span>
            );
        }

        return <div className="review-stars-container" onClick={(e) => e.stopPropagation()}>{stars}</div>;
    };

    renderStylistStep = () => {
        const { stylistRating, stylistReview, stylistHover } = this.state;
        const { booking } = this.props;
        // Support both v2 API (hairdresserInfo) and legacy API (hairdresser)
        const hairdresser = booking.hairdresserInfo || booking.hairdresser || {};
        
        // Get stylist image - v2 API returns full S3 URLs for userImage.circleImage and userImage.squareImage
        const stylistImage = hairdresser.userImage?.circleImage || hairdresser.userImage?.squareImage || null;

        const getInitials = (name) => {
            if (!name) return '?';
            return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        };

        return (
            <div className="review-step">
                <div className="review-step-header">
                    <div className="review-step-icon">
                        {stylistImage ? (
                            <img src={stylistImage} alt={hairdresser.name} className="review-avatar" />
                        ) : (
                            <div className="review-avatar-placeholder">
                                {getInitials(hairdresser.name)}
                            </div>
                        )}
                    </div>
                    <h2 className="review-step-title">How was your stylist?</h2>
                    <p className="review-step-subtitle">{hairdresser.name || 'Your Stylist'}</p>
                </div>

                <div className="review-rating-section">
                    {this.renderStars(
                        stylistRating,
                        stylistHover,
                        this.handleStylistRating,
                        this.handleStylistHover
                    )}
                    <p className="review-rating-label">
                        {stylistRating === 0 ? 'Tap to rate' : this.getRatingLabel(stylistRating)}
                    </p>
                </div>

                <div className="review-text-section">
                    <label className="review-text-label">Share your experience (optional)</label>
                    <textarea
                        className="review-textarea"
                        placeholder="Tell us about your experience with your stylist..."
                        value={stylistReview}
                        onChange={(e) => this.setState({ stylistReview: e.target.value })}
                        maxLength={1000}
                    />
                    <span className="review-char-count">{stylistReview.length}/1000</span>
                </div>

                <div className="review-actions">
                    <button 
                        className="review-btn review-btn-secondary"
                        onClick={this.props.onClose}
                    >
                        Skip
                    </button>
                    <button 
                        className="review-btn review-btn-primary"
                        onClick={this.handleNextStep}
                        disabled={stylistRating === 0}
                    >
                        Next
                    </button>
                </div>
            </div>
        );
    };

    renderSalonStep = () => {
        const { salonRating, salonReview, salonHover, submitting } = this.state;
        const { booking } = this.props;
        const salon = booking.salonInfo || {};
        
        // Get salon image - v2 API returns full S3 URLs for salonImage and image
        const salonImage = salon.salonImage || salon.image || null;

        const getInitials = (name) => {
            if (!name) return '?';
            return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        };

        return (
            <div className="review-step">
                <div className="review-step-header">
                    <div className="review-step-icon">
                        {salonImage ? (
                            <img src={salonImage} alt={salon.name} className="review-avatar salon" />
                        ) : (
                            <div className="review-avatar-placeholder salon">
                                {getInitials(salon.name)}
                            </div>
                        )}
                    </div>
                    <h2 className="review-step-title">How was the salon?</h2>
                    <p className="review-step-subtitle">{salon.name || salon.salonName || 'The Venue'}</p>
                </div>

                <div className="review-rating-section">
                    {this.renderStars(
                        salonRating,
                        salonHover,
                        this.handleSalonRating,
                        this.handleSalonHover
                    )}
                    <p className="review-rating-label">
                        {salonRating === 0 ? 'Tap to rate' : this.getRatingLabel(salonRating)}
                    </p>
                </div>

                <div className="review-text-section">
                    <label className="review-text-label">Share your experience (optional)</label>
                    <textarea
                        className="review-textarea"
                        placeholder="Tell us about your experience at the salon..."
                        value={salonReview}
                        onChange={(e) => this.setState({ salonReview: e.target.value })}
                        maxLength={1000}
                    />
                    <span className="review-char-count">{salonReview.length}/1000</span>
                </div>

                <div className="review-actions">
                    <button 
                        className="review-btn review-btn-secondary"
                        onClick={this.handlePrevStep}
                        disabled={submitting}
                    >
                        Back
                    </button>
                    <button 
                        className="review-btn review-btn-primary"
                        onClick={this.handleSubmit}
                        disabled={salonRating === 0 || submitting}
                    >
                        {submitting ? 'Submitting...' : 'Submit Review'}
                    </button>
                </div>
            </div>
        );
    };

    renderSuccessStep = () => {
        return (
            <div className="review-step review-success">
                <div className="review-success-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#10B981"/>
                        <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
                <h2 className="review-success-title">Thank you!</h2>
                <p className="review-success-message">
                    Your review has been submitted successfully. Your feedback helps others find great stylists and salons.
                </p>
                <button 
                    className="review-btn review-btn-primary"
                    onClick={this.props.onClose}
                >
                    Done
                </button>
            </div>
        );
    };

    getRatingLabel = (rating) => {
        const labels = {
            1: 'Poor',
            2: 'Fair',
            3: 'Good',
            4: 'Very Good',
            5: 'Excellent'
        };
        return labels[rating] || '';
    };

    render() {
        const { step } = this.state;
        const { isOpen, onClose } = this.props;

        if (!isOpen) return null;

        return (
            <div className="review-modal-overlay" onClick={onClose}>
                <div className="review-modal" onClick={(e) => e.stopPropagation()}>
                    {/* Back arrow button on left side */}
                    <button className="review-modal-back" onClick={onClose}>
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>

                    {/* Progress indicator */}
                    {step < 3 && (
                        <div className="review-progress">
                            <div className={`review-progress-step ${step >= 1 ? 'active' : ''}`}>
                                <span>1</span>
                            </div>
                            <div className="review-progress-line"></div>
                            <div className={`review-progress-step ${step >= 2 ? 'active' : ''}`}>
                                <span>2</span>
                            </div>
                        </div>
                    )}

                    {step === 1 && this.renderStylistStep()}
                    {step === 2 && this.renderSalonStep()}
                    {step === 3 && this.renderSuccessStep()}
                </div>
            </div>
        );
    }
}

export default ReviewModal;
